/**
 * RejillaAnualTaquilla.jsx · la taquilla de una clienta, AÑO A AÑO.
 *
 * ═══════════════════════════ CONTRATO ═══════════════════════════
 * QUÉ ES (Ann, 01-10-2026): «en la ficha de cliente, en taquillas, una rejilla
 *   como la de clases fijas pero para las taquillas, que se renueva anualmente
 *   con el precio vigente de la taquilla».
 *   La de clases fijas es por MESES porque se paga cada mes. La taquilla se paga
 *   una vez al año, así que **la fila es el AÑO**. Es lo único que cambia.
 *
 * NO SE PUEDE CAMBIAR:
 *   · **No calcula nada.** Los importes, los estados y el precio de la próxima
 *     renovación los da el servidor (`GET /taquillas/cliente/{id}/rejilla`). Si
 *     los calculara aquí, un día enseñaría un precio y se cobraría otro.
 *   · **Los estados se importan de `estadosCobro.js`.** No se escribe otra tabla:
 *     ya había tres para lo mismo y el mismo cobro se llamaba de dos formas según
 *     la pantalla.
 *   · **Un hueco se enseña como hueco.** Si el servidor dice que un año no tiene
 *     cobro, se ve y se explica. No se pinta como cobrado.
 *
 * POR QUÉ ES UN FICHERO APARTE: `ClientNewProfile.jsx` tiene más de 9.000 líneas
 *   y solo puede encoger. Nada nuevo nace ahí dentro.
 * ════════════════════════════════════════════════════════════════
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../../api";
import { Loader2 } from "lucide-react";
import { estiloDeEstado, MET_FULL } from "./estadosCobro";

const eur = (n) =>
  Number(n || 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";

const dia = (s) => {
  if (!s) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : String(s).slice(0, 10);
};

const Badge = ({ estado, aviso }) => {
  // Sin estado el servidor manda su motivo: «sin cobro registrado» (falta algo) o
  // «sin cargo (0 €)» (no se cobró a propósito). Son casos distintos y se ven distinto.
  if (!estado) {
    const falta = aviso === "sin cobro registrado";
    return (
      <span style={{ ...S.badge, color: falta ? "#ffffff" : "#6b7280",
                     background: falta ? "#dc2626" : "#f3f4f6" }}>
        {falta ? "⚠️ sin cobro" : "sin cargo"}
      </span>
    );
  }
  const e = estiloDeEstado(estado);
  return <span style={{ ...S.badge, color: e.c, background: e.bg }}>{e.l}</span>;
};

const S = {
  tabla: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: { textAlign: "left", padding: "6px 8px", borderBottom: "2px solid #e5e7eb",
        fontSize: 11, textTransform: "uppercase", letterSpacing: ".04em", color: "#6b7280" },
  td: { padding: "7px 8px", borderBottom: "1px solid #f3f4f6" },
  badge: { display: "inline-block", padding: "1px 7px", borderRadius: 3,
           fontSize: 11, fontWeight: 600, lineHeight: "17px", whiteSpace: "nowrap" },
};

const RejillaAnualTaquilla = ({ clienteId }) => {
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!clienteId) return;
    let vivo = true;
    setCargando(true);
    setError(null);
    axios.get(`${API}/taquillas/cliente/${clienteId}/rejilla`)
      .then((r) => vivo && setDatos(r.data))
      // Un fallo se DICE. Si se tragara, la rejilla saldría vacía y parecería
      // que la clienta no ha tenido taquilla nunca.
      .catch(() => vivo && setError("No se ha podido cargar el histórico de taquilla."))
      .finally(() => vivo && setCargando(false));
    return () => { vivo = false; };
  }, [clienteId]);

  if (cargando) {
    return <div style={{ padding: 12, color: "#6b7280", fontSize: 13 }}>
      <Loader2 size={14} style={{ display: "inline", marginRight: 6 }} /> Cargando taquilla…
    </div>;
  }
  if (error) {
    return <div style={{ padding: 12, fontSize: 13, color: "#991b1b", background: "#fef2f2",
                         border: "1px solid #fecaca", borderRadius: 6 }}>{error}</div>;
  }
  if (!datos || (!datos.filas?.length && !datos.prevision)) {
    return <div style={{ padding: 12, color: "#6b7280", fontSize: 13 }}>
      Esta clienta no tiene ni ha tenido taquilla.
    </div>;
  }

  const p = datos.prevision;

  return (
    <div>
      <table style={S.tabla}>
        <thead>
          <tr>
            <th style={S.th}>Año</th>
            <th style={S.th}>Taquilla</th>
            <th style={S.th}>Periodo</th>
            <th style={{ ...S.th, textAlign: "right" }}>Importe</th>
            <th style={S.th}>Cómo</th>
            <th style={S.th}>Estado</th>
          </tr>
        </thead>
        <tbody>
          {p && (
            <tr style={{ background: "#faf5ff" }}>
              <td style={{ ...S.td, fontWeight: 700 }}>{p.anyo ?? "—"}</td>
              <td style={S.td}>{p.taquilla}</td>
              <td style={S.td}>desde {dia(p.periodo_desde)}</td>
              <td style={{ ...S.td, textAlign: "right", fontWeight: 600 }}>
                {eur(p.importe)}
                {p.sube && (
                  <div style={{ fontSize: 11, color: "#b45309", fontWeight: 500 }}>
                    ⬆️ antes {eur(p.importe_actual)}
                  </div>
                )}
              </td>
              <td style={S.td}>{MET_FULL[p.forma_pago] || p.forma_pago || "—"}</td>
              <td style={S.td}>
                <span style={{ ...S.badge, color: "#6d28d9", background: "#ede9fe" }}>
                  próxima renovación
                </span>
                {p.aviso && (
                  <div style={{ fontSize: 11, color: "#991b1b", marginTop: 3 }}>⚠️ {p.aviso}</div>
                )}
              </td>
            </tr>
          )}
          {datos.filas.map((f) => (
            <tr key={`${f.anyo}-${f.taquilla}`}>
              <td style={{ ...S.td, fontWeight: 700 }}>{f.anyo}</td>
              <td style={S.td}>{f.taquilla}</td>
              <td style={S.td}>{dia(f.periodo_desde)} – {dia(f.periodo_hasta)}</td>
              <td style={{ ...S.td, textAlign: "right" }}>{eur(f.importe)}</td>
              <td style={S.td}>{MET_FULL[f.forma_pago] || f.forma_pago || "—"}</td>
              <td style={S.td}>
                <Badge estado={f.estado} aviso={f.aviso} />
                {!f.cerrado && (
                  <span style={{ ...S.badge, marginLeft: 4, color: "#166534", background: "#dcfce7" }}>
                    en curso
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p style={{ fontSize: 11, color: "#6b7280", marginTop: 8 }}>
        La taquilla se renueva una vez al año. El precio de la próxima renovación es
        el vigente en Taquillas → Configuración, no el que se pagó el año anterior.
      </p>
    </div>
  );
};

export default RejillaAnualTaquilla;
